import api from './api';

export interface SkuDetail {
    id: number;
    itemId: number;
    stockQuantity: number;
    stockFreeQuantity: number;
    price: number;
    mrp: number;
    skuNo: string;
    barCode: string;
    supplierName: string;
    supplierId: number;
    cost: number;
    costWithoutTax: number;
    minSellingPrice: number;
    locationId: number;
    locationName: string;
    expiresAt: string;
    purchaseDate: string;
    [key: string]: unknown;
}

export interface Product {
    productId: number;
    name: string;
    aliasName?: string;
    isOffer?: number;
    offerFreeQty?: number;
    offerQty?: number;
    offerRate?: number;
    status?: string;
    categoryId: number;
    categoryName: string;
    productType?: string;
    itemType?: number;
    verticalType?: number;
    eanCode?: string;
    brandName?: string;
    brandId?: number;
    expiryFormat?: string;
    isInclusiveTax?: string;
    stock: number;
    price: number;
    mrp: number;
    taxId?: number;
    syncTS?: number;
    imageUrl?: string | string[];
    decimalDigit?: number;
    cost?: number;
    costWithoutTax?: number;
    description?: string;
    gstCalculationBasedOn?: string;
    hsnCode?: string;
    taxType?: string;
    isGstExempted?: boolean;
    totalStock?: number;
    itemMasterRate?: number;
    itemMasterMrp?: number;
    supplierName?: string;
    skuDetails?: SkuDetail[];
    createdAt?: string;
    updatedAt?: string;
    id?: number;
    documentId?: string;
    isBestSeller?: boolean;
    [key: string]: unknown;
}

export interface ProductListData {
    items: Product[];
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    };
}

export interface ProductResponse {
    success: boolean;
    message?: string;
    data?: ProductListData | Product[];
    timestamp?: string;
    statusCode?: number;
}

export interface SyncResponse {
    success: boolean;
    message?: string;
    data?: {
        synced?: number;
        total?: number;
        remaining?: number;
        failed?: number;
        products?: Product[];
    };
    timestamp?: string;
    statusCode?: number;
}

// Sync products from external source
export const syncProducts = async (): Promise<SyncResponse> => {
    const response = await api.post('/product/sync');
    return response.data;
};

// Get all product names (lightweight — only productId + name)
export const getProductNames = async (categoryName?: string): Promise<{ success: boolean; data?: Array<{ productId: number; name: string }> }> => {
    const response = await api.get('/product/names', { params: { categoryName } });
    return response.data;
};

// Get all unique category names
export const getCategoryNames = async (): Promise<{ success: boolean; data?: string[] }> => {
    const response = await api.get('/product/categories');
    return response.data;
};

// Get all products with optional pagination
export const getProducts = async (params?: {
    page?: number;
    limit?: number;
    search?: string;
}): Promise<ProductResponse> => {
    const response = await api.get('/product', { params });
    return response.data;
};

// Get single product by ID
export const getProductById = async (id: string | number): Promise<{ success: boolean; data?: Product }> => {
    const response = await api.get(`/product/${id}`);
    return response.data;
};

// Toggle Best Seller for a product - PATCH /api/site/product/:id/toggle-bestseller
export const toggleBestSeller = async (id: string | number): Promise<{ success: boolean; message?: string; data?: Product }> => {
    const response = await api.patch(`/product/${id}/toggle-bestseller`);
    return response.data;
};

// Upload product images - PUT /api/site/product/:id/image
export const uploadProductImages = async (id: string, files: File[], customNames?: string[]): Promise<{ success: boolean; message?: string; data?: Product }> => {
    const formData = new FormData();
    files.forEach((file, index) => {
        formData.append('images', file, (customNames && customNames[index]) || file.name);
    });
    const response = await api.put(`/product/${id}/image`, formData);
    return response.data;
};

// Delete product image - DELETE /api/site/product/:id/image
export const deleteProductImage = async (id: string | number, imageUrl?: string): Promise<{ success: boolean; message?: string; data?: Product }> => {
    const response = await api.delete(`/product/${id}/image`, { data: { imageUrl } });
    return response.data;
};

// Get sync status
export const getSyncStatus = async (): Promise<{
    success: boolean;
    data: {
        isRunning: boolean;
        progress: {
            totalPages: number;
            processedPages: number;
            totalFetched: number;
            created: number;
            updated: number;
            failed: number;
        };
        lastError?: string;
    }
}> => {
    const response = await api.get('/product/sync/status');
    return response.data;
};

